import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// A no-op in dev (devOptions.enabled is left off — see vite.config.js) so
// this import is always safe; only does anything in a built/installed app.
import { registerSW } from 'virtual:pwa-register';
import App from './App.jsx';
import { ApiClientError } from './lib/api.js';
import './styles/index.css';

// autoUpdate (vite.config.js) means a new build activates on its own the
// next time the app opens — this prompt only covers the rarer case where
// it's already open on the counter when a new version lands, so a bill
// mid-entry never gets swapped out from under someone without asking.
const updateSW = registerSW({
  onNeedRefresh() {
    if (window.confirm('An update to the app is ready. Reload now to get it?')) {
      updateSW(true);
    }
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Shop data changes on a human timescale; a minute of staleness saves a lot
      // of mobile data and makes repeat screens feel instant.
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry(failureCount, error) {
        // Retrying a 4xx just repeats the same mistake. Network blips are worth
        // a couple of attempts — a phone on shop Wi-Fi drops packets.
        if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Opting into the v7 behaviors now avoids both a console warning today
          and a breaking change whenever this project upgrades React Router. */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
